// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Car, Plus, Search, Trash2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import {
  Button,
  Card,
  Badge,
  EmptyState,
  SetupGuide,
  Select,
  Pagination,
  DataTable,
  Skeleton,
  Input,
  ConfirmDialog,
} from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { useVehicles, useDeleteVehicle } from '@/hooks/useVehicles'
import { useListSort } from '@/hooks/useListSort'
import { useProperty } from '@/hooks/useProperties'
import type { Vehicle, VehicleType } from '@/types/parking'

export function VehicleListPage() {
  const { t } = useTranslation('parking')
  const { t: tc } = useTranslation('common')
  const { propertyId: propertyIdParam } = useParams<{ propertyId: string }>()
  const propertyId = Number(propertyIdParam)
  const navigate = useNavigate()

  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [vehicleType, setVehicleType] = useState<VehicleType | ''>('')
  const [deleteTarget, setDeleteTarget] = useState<Vehicle | null>(null)
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)

  // 300ms debounce for the free-text search (plate/brand/model/color/customer)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // EP-97: default-rule page — updated_at desc, user-selectable server-side sort.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'updated_at',
    defaultOrder: 'desc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, error, isFetching } = useVehicles(propertyId, {
    page,
    q: debouncedSearch || undefined,
    vehicle_type: vehicleType || undefined,
    ...sortParams,
  })

  const sortOptions = [
    { value: 'updated_at', label: tc('sort.by_last_updated') },
    { value: 'license_plate', label: tc('sort.by_plate') },
    { value: 'vehicle_type', label: t('vehicles.vehicle_type') },
    { value: 'created_at', label: tc('sort.by_newest') },
  ]

  // Unfiltered query to decide between setup guide and empty state
  const { data: totalData } = useVehicles(propertyId, { page: 1 })
  const totalCount = totalData?.meta?.total_count ?? 0

  const { data: property } = useProperty(propertyId)
  const deleteVehicle = useDeleteVehicle()

  const vehicles = data?.data || []

  const vehicleTypeOptions = [
    { value: '', label: t('filters.all_types') },
    { value: 'car', label: t('vehicles.types.car') },
    { value: 'motorcycle', label: t('vehicles.types.motorcycle') },
    { value: 'pickup', label: t('vehicles.types.pickup') },
    { value: 'bicycle', label: t('vehicles.types.bicycle') },
    { value: 'van', label: t('vehicles.types.van') },
  ]

  const getVehicleTypeBadge = (type: VehicleType) => {
    switch (type) {
      case 'car':
        return <Badge variant="info">{t('vehicles.types.car')}</Badge>
      case 'motorcycle':
        return <Badge variant="warning">{t('vehicles.types.motorcycle')}</Badge>
      case 'pickup':
        return <Badge variant="ghost">{t('vehicles.types.pickup')}</Badge>
      case 'bicycle':
        return <Badge variant="success">{t('vehicles.types.bicycle')}</Badge>
      case 'van':
        return <Badge variant="ghost">{t('vehicles.types.van')}</Badge>
      default:
        return <Badge variant="ghost">{type}</Badge>
    }
  }

  const getPermitStatusBadge = (vehicle: Vehicle) => {
    const hasActivePermit = vehicle.active_permits && vehicle.active_permits.length > 0
    if (hasActivePermit) {
      return <Badge variant="success">{t('permits.statuses.active')}</Badge>
    }
    return <span className="text-base-content/50">-</span>
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    await deleteVehicle.mutateAsync(deleteTarget.id)
    setDeleteTarget(null)
  }

  const hasActivePermits = (vehicle: Vehicle) =>
    vehicle.active_permits != null && vehicle.active_permits.length > 0

  const columns: Column<Vehicle>[] = [
    {
      key: 'license_plate',
      header: t('vehicles.license_plate'),
      sortable: true,
      render: (v) => <span className="font-medium font-mono">{v.license_plate}</span>,
    },
    {
      key: 'vehicle_type',
      header: t('vehicles.vehicle_type'),
      sortable: true,
      render: (v) => getVehicleTypeBadge(v.vehicle_type),
    },
    {
      key: 'brand_model',
      header: `${t('vehicles.brand')} / ${t('vehicles.model')}`,
      render: (v) => {
        const parts = [v.brand, v.model].filter(Boolean)
        return <span>{parts.length ? parts.join(' ') : '-'}</span>
      },
    },
    {
      key: 'color',
      header: t('vehicles.color'),
      render: (v) => <span>{v.color || '-'}</span>,
    },
    {
      key: 'customer',
      header: tc('nav.customers'),
      render: (v) =>
        v.customer ? (
          <Link
            to={`/customers/${v.customer.id}`}
            className="link link-primary"
            onClick={(e) => e.stopPropagation()}
          >
            {v.customer.first_name} {v.customer.last_name}
          </Link>
        ) : (
          <span className="text-base-content/50">-</span>
        ),
    },
    {
      key: 'permit_status',
      header: tc('labels.status'),
      render: (v) => getPermitStatusBadge(v),
    },
    {
      key: 'actions',
      header: '',
      render: (v) => (
        <Button
          size="sm"
          variant="ghost"
          className="text-error"
          disabled={hasActivePermits(v)}
          title={hasActivePermits(v) ? t('vehicles.delete_has_permits') : tc('actions.delete')}
          onClick={(e) => {
            e.stopPropagation()
            if (!hasActivePermits(v)) {
              setDeleteTarget(v)
            }
          }}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      ),
    },
  ]

  const activeVehicleFilterCount = [vehicleType].filter(Boolean).length

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs
          items={[
            { label: tc('nav.properties'), href: '/properties' },
            { label: property?.name || '...', href: `/properties/${propertyId}` },
            { label: t('vehicles.title') },
          ]}
        />

        <PageHeader
          title={t('vehicles.title')}
          isRefreshing={isFetching && !isLoading}
          actions={
            <Button
              size="sm"
              leftIcon={<Plus className="w-4 h-4" />}
              onClick={() => navigate(`/properties/${propertyId}/parking/vehicles/new`)}
            >
              {t('vehicles.add')}
            </Button>
          }
        />

        {/* Filters */}
        <Card>
          {/* Search — always visible */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('vehicles.search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          {/* Mobile-only toggle */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="vehicle-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeVehicleFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeVehicleFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="vehicle-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            <Select
              label={t('vehicles.vehicle_type')}
              value={vehicleType}
              onChange={(e) => {
                setVehicleType(e.target.value as VehicleType | '')
                setPage(1)
              }}
              options={vehicleTypeOptions}
              className="w-full sm:w-48 min-w-0"
            />
          </div>
        </Card>

        {/* Content */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={tc('errors.something_went_wrong')}
              description={tc('errors.something_went_wrong')}
              action={
                <Button onClick={() => window.location.reload()}>
                  {tc('actions.retry')}
                </Button>
              }
            />
          </Card>
        ) : !vehicles.length ? (
          <Card>
            {totalCount === 0 ? (
              <SetupGuide
                icon={<Car className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prereq_property'),
                    met: !!property,
                    link: '/properties',
                    linkLabel: t('setup_guide.prereq_setup_property'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  {
                    title: t('setup_guide.steps.create_zone.title'),
                    description: t('setup_guide.steps.create_zone.description'),
                  },
                  {
                    title: t('setup_guide.steps.register_vehicle.title'),
                    description: t('setup_guide.steps.register_vehicle.description'),
                  },
                  {
                    title: t('setup_guide.steps.issue_permit.title'),
                    description: t('setup_guide.steps.issue_permit.description'),
                  },
                ]}
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/properties/${propertyId}/parking/vehicles/new`)}
                  >
                    {t('vehicles.add')}
                  </Button>
                }
              />
            ) : (
              <EmptyState
                icon={<Car className="w-12 h-12" />}
                title={t('vehicles.empty.title')}
                description={
                  debouncedSearch || vehicleType
                    ? t('vehicles.empty.no_search_results')
                    : t('vehicles.empty.description')
                }
                action={
                  <Button
                    leftIcon={<Plus className="w-4 h-4" />}
                    onClick={() => navigate(`/properties/${propertyId}/parking/vehicles/new`)}
                  >
                    {t('vehicles.add')}
                  </Button>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            <DataTable
              columns={columns}
              data={vehicles}
              keyExtractor={(v) => v.id}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
              onRowClick={(v) => navigate(`/properties/${propertyId}/parking/vehicles/${v.id}/edit`)}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Delete Confirm Dialog */}
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={tc('actions.delete')}
        message={t('vehicles.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteVehicle.isPending}
      />
    </>
  )
}
